import React from 'react';
import clsx from 'clsx';
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const CATALOG_ADD_CLASS = clsx(
  'shrink-0 flex items-center gap-1 rounded px-1.5 py-1 text-[10px] font-medium border transition-colors',
  'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 hover:bg-sky-50 dark:hover:bg-sky-500/10 hover:text-sky-700 dark:hover:text-sky-300 hover:border-sky-200 dark:hover:border-sky-500/30'
);

export type WorkspacePdfFile = {
  id: string;
  kind?: 'file' | 'artifact';
  name: string;
  mimeType?: string | null;
  /** Optional download URL; kept for catalog passthrough / future "open file" UX (not rendered today). */
  url?: string | null;
};

function isPdfWorkspaceFile(f: WorkspacePdfFile): boolean {
  if (f.kind === 'artifact') {
    return true;
  }
  return f.mimeType === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
}

function isPdfFile(f: File): boolean {
  return f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
}

/** Pure helper for tests and single-placement add behavior. */
export function resolveAddPlacement(
  id: string,
  section: 'before' | 'after',
  prefixFileIds: string[],
  suffixFileIds: string[]
): { type: 'noop' } | { type: 'append' } | { type: 'move'; from: 'before' | 'after' } {
  const inBefore = prefixFileIds.includes(id);
  const inAfter = suffixFileIds.includes(id);
  if (section === 'before') {
    if (inBefore) return { type: 'noop' };
    if (inAfter) return { type: 'move', from: 'after' };
    return { type: 'append' };
  }
  if (inAfter) return { type: 'noop' };
  if (inBefore) return { type: 'move', from: 'before' };
  return { type: 'append' };
}

export type PdfAttachmentSectionProps = {
  /** PDFs the user can attach: workspace files, message files, and template prefix/suffix (caller merges into one map). */
  attachmentCatalog: WorkspacePdfFile[];
  prefixFileIds: string[];
  suffixFileIds: string[];
  onChange: (prefixFileIds: string[], suffixFileIds: string[]) => void;
  /** Called whenever the map of locally-held File objects changes (used by parent to build the render mutation). */
  onLocalFilesChange?: (localFiles: Map<string, File>) => void;
  /**
   * The document the two sections sit either side of. The editor leaves this
   * unset because the document is on screen behind the panel; a caller whose
   * document is not otherwise visible names it here.
   */
  documentName?: string;
  /**
   * Takes dropped and chosen files instead of the section holding them. A
   * caller that stores its files somewhere — an upload, say — needs the id it
   * stored them under, not the temp one a locally-held file gets, and the
   * section says which side of the document they were dropped on.
   */
  onAddFiles?: (files: File[], section: 'before' | 'after') => void;
  /** Wording on the drop zone, for callers whose merge is not an export. */
  dropZoneLabel?: string;
  /** Heading over the list of catalog entries not yet placed. */
  catalogLabel?: string;
};

export const PdfAttachmentSection: React.FC<PdfAttachmentSectionProps> = ({
  attachmentCatalog,
  prefixFileIds,
  suffixFileIds,
  onChange,
  onLocalFilesChange,
  documentName,
  onAddFiles,
  dropZoneLabel = 'Add PDF (this export only)',
  catalogLabel = 'Add from workspace'
}) => {
  // localFiles holds File objects for user-dropped PDFs, keyed by a temp ID.
  // These are not uploaded to the server; they are included as Upload inputs in the render mutation.
  const [localFiles, setLocalFiles] = React.useState<Map<string, File>>(() => new Map());
  const [draggingOver, setDraggingOver] = React.useState<'before' | 'after' | null>(null);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  const beforeInputRef = React.useRef<HTMLInputElement>(null);
  const afterInputRef = React.useRef<HTMLInputElement>(null);
  const validationErrorTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const onLocalFilesChangeRef = React.useRef(onLocalFilesChange);
  React.useEffect(() => {
    onLocalFilesChangeRef.current = onLocalFilesChange;
  }, [onLocalFilesChange]);

  React.useEffect(() => {
    return () => {
      if (validationErrorTimerRef.current) {
        clearTimeout(validationErrorTimerRef.current);
        validationErrorTimerRef.current = null;
      }
    };
  }, []);

  const workspacePdfs = attachmentCatalog.filter(
    (f) => isPdfWorkspaceFile(f) && !prefixFileIds.includes(f.id) && !suffixFileIds.includes(f.id)
  );

  const moveToOtherSection = React.useCallback(
    (id: string, from: 'before' | 'after') => {
      if (from === 'before') {
        onChange(
          prefixFileIds.filter((x) => x !== id),
          [...suffixFileIds, id]
        );
      } else {
        onChange(
          [...prefixFileIds, id],
          suffixFileIds.filter((x) => x !== id)
        );
      }
    },
    [prefixFileIds, suffixFileIds, onChange]
  );

  const addToSection = React.useCallback(
    (id: string, section: 'before' | 'after') => {
      const decision = resolveAddPlacement(id, section, prefixFileIds, suffixFileIds);
      if (decision.type === 'noop') {
        return;
      }
      if (decision.type === 'move') {
        moveToOtherSection(id, decision.from);
        return;
      }
      if (section === 'before') {
        onChange([...prefixFileIds, id], suffixFileIds);
      } else {
        onChange(prefixFileIds, [...suffixFileIds, id]);
      }
    },
    [prefixFileIds, suffixFileIds, onChange, moveToOtherSection]
  );

  const removeFromSection = (id: string, section: 'before' | 'after') => {
    // If the ID is a local file, remove it from the local files map as well.
    if (localFiles.has(id)) {
      setLocalFiles((prev) => {
        const next = new Map(prev);
        next.delete(id);
        onLocalFilesChangeRef.current?.(next);
        return next;
      });
    }
    if (section === 'before')
      onChange(
        prefixFileIds.filter((x) => x !== id),
        suffixFileIds
      );
    else
      onChange(
        prefixFileIds,
        suffixFileIds.filter((x) => x !== id)
      );
  };

  const reorder = (section: 'before' | 'after', idx: number, dir: 'up' | 'down') => {
    const arr = section === 'before' ? [...prefixFileIds] : [...suffixFileIds];
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= arr.length) return;
    [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
    if (section === 'before') {
      onChange(arr, suffixFileIds);
    } else {
      onChange(prefixFileIds, arr);
    }
  };

  const showValidationError = (msg: string) => {
    setValidationError(msg);
    if (validationErrorTimerRef.current) clearTimeout(validationErrorTimerRef.current);
    validationErrorTimerRef.current = setTimeout(() => setValidationError(null), 5000);
  };

  const onAddFilesRef = React.useRef(onAddFiles);
  React.useEffect(() => {
    onAddFilesRef.current = onAddFiles;
  }, [onAddFiles]);

  const addLocalFile = React.useCallback(
    (file: File, section: 'before' | 'after') => {
      if (!isPdfFile(file)) {
        showValidationError('Only PDF files are supported.');
        return;
      }
      if (file.size === 0) {
        showValidationError(`"${file.name}" is empty. Please upload a readable file.`);
        return;
      }

      // A caller that stores the file gets to place it itself: the id it ends
      // up with is the stored one, and a temp id would name a file nobody but
      // this component can resolve.
      if (onAddFilesRef.current) {
        onAddFilesRef.current([file], section);
        return;
      }

      const tempId = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      setLocalFiles((prev) => {
        const next = new Map(prev);
        next.set(tempId, file);
        onLocalFilesChangeRef.current?.(next);
        return next;
      });

      addToSection(tempId, section);
    },
    [addToSection]
  );

  const onDragEnter = (section: 'before' | 'after') => (e: React.DragEvent) => {
    e.preventDefault();
    setDraggingOver(section);
  };

  const onDragLeave = (section: 'before' | 'after') => (e: React.DragEvent) => {
    e.preventDefault();
    if (e.relatedTarget && (e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
      return;
    }
    setDraggingOver((cur) => (cur === section ? null : cur));
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDrop = (section: 'before' | 'after') => (e: React.DragEvent) => {
    e.preventDefault();
    setDraggingOver(null);
    Array.from(e.dataTransfer.files).forEach((f) => addLocalFile(f, section));
  };

  const renderFileChip = (id: string, section: 'before' | 'after', idx: number, total: number) => {
    const localFile = localFiles.get(id);
    const catalogItem = attachmentCatalog.find((f) => f.id === id);
    const name = localFile?.name ?? catalogItem?.name ?? id;

    return (
      <div
        key={id}
        className="flex items-center gap-2 rounded-lg border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 bg-white dark:bg-neutral-800 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-col min-w-0">
            <p className="truncate text-xs min-w-0" title={name}>
              {name}
            </p>
            {catalogItem?.kind === 'artifact' && (
              <span className="text-[10px] text-neutral-400 dark:text-neutral-500 leading-none">
                Document
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => reorder(section, idx, 'up')}
            disabled={idx === 0}
            className="p-0.5 rounded text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Move up"
          >
            <ArrowUpIcon className="size-3" />
          </button>
          <button
            type="button"
            onClick={() => reorder(section, idx, 'down')}
            disabled={idx === total - 1}
            className="p-0.5 rounded text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Move down"
          >
            <ArrowDownIcon className="size-3" />
          </button>
          <button
            type="button"
            onClick={() => moveToOtherSection(id, section)}
            className="p-0.5 rounded text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
            title={section === 'before' ? 'Move to After document' : 'Move to Before document'}
          >
            {section === 'before' ? (
              <ArrowRightIcon className="size-3" />
            ) : (
              <ArrowLeftIcon className="size-3" />
            )}
          </button>
          <button
            type="button"
            onClick={() => removeFromSection(id, section)}
            className="p-0.5 rounded text-neutral-400 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
            title="Remove"
          >
            <XMarkIcon className="size-3" />
          </button>
        </div>
      </div>
    );
  };

  const renderDropZone = (section: 'before' | 'after') => {
    const isDragging = draggingOver === section;
    const inputRef = section === 'before' ? beforeInputRef : afterInputRef;
    return (
      <div
        onDragEnter={onDragEnter(section)}
        onDragLeave={onDragLeave(section)}
        onDragOver={onDragOver}
        onDrop={onDrop(section)}
        onClick={() => inputRef.current?.click()}
        className={clsx(
          'flex items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-2 text-xs transition-colors',
          isDragging
            ? 'border-sky-400 bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 cursor-pointer'
            : 'border-neutral-200 dark:border-neutral-700 text-neutral-400 dark:text-neutral-500 hover:border-neutral-300 dark:hover:border-white/20 hover:text-neutral-600 dark:hover:text-neutral-400 cursor-pointer'
        )}
      >
        <CloudArrowUpIcon className="size-3.5 shrink-0" />
        <span>{isDragging ? 'Drop PDF here' : dropZoneLabel}</span>
      </div>
    );
  };

  const renderSection = (section: 'before' | 'after') => {
    const ids = section === 'before' ? prefixFileIds : suffixFileIds;
    const label = section === 'before' ? 'Before document' : 'After document';

    return (
      <div>
        <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1.5">{label}</p>
        <div className="space-y-1.5">
          {ids.map((id, idx) => renderFileChip(id, section, idx, ids.length))}
          {renderDropZone(section)}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <input
        ref={beforeInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={(e) => {
          Array.from(e.currentTarget.files ?? []).forEach((f) => addLocalFile(f, 'before'));
          e.currentTarget.value = '';
        }}
      />
      <input
        ref={afterInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={(e) => {
          Array.from(e.currentTarget.files ?? []).forEach((f) => addLocalFile(f, 'after'));
          e.currentTarget.value = '';
        }}
      />

      {validationError && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2 text-xs text-red-600 dark:text-red-400">
          <ExclamationCircleIcon className="size-4 shrink-0" />
          {validationError}
        </div>
      )}

      <div className="space-y-3 max-h-[min(50vh,22rem)] overflow-y-auto pr-0.5">
        {renderSection('before')}
        {documentName && (
          <div>
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1.5">
              Document
            </p>
            <div className="flex items-center gap-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-2.5 py-1.5">
              <DocumentTextIcon className="size-3.5 shrink-0 text-neutral-400 dark:text-neutral-500" />
              <p className="truncate text-xs" title={documentName}>
                {documentName}
              </p>
            </div>
          </div>
        )}
        {renderSection('after')}
      </div>

      {workspacePdfs.length > 0 && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3">
          <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1.5">
            {catalogLabel}
          </p>
          <div className="space-y-1 max-h-[120px] overflow-y-auto">
            {workspacePdfs.map((f) => {
              return (
                <div key={f.id} className="flex items-center gap-1.5">
                  {/* The name truncates on its own element, not on the row:
                      text-overflow has nothing to act on in a flex container,
                      where the text is an anonymous item, so a long name set
                      to ellipsis here would instead be cut mid-letter. */}
                  <span
                    className="flex-1 min-w-0 text-xs px-2 py-1 rounded border inline-flex items-center gap-1.5"
                    title={f.name}
                  >
                    {f.kind === 'artifact' && <DocumentTextIcon className="size-3 shrink-0" />}
                    <span className="truncate">{f.name}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => addToSection(f.id, 'before')}
                    className={CATALOG_ADD_CLASS}
                    title="Add before document"
                  >
                    <ArrowLeftIcon className="size-3" />
                    Before
                  </button>
                  <button
                    type="button"
                    onClick={() => addToSection(f.id, 'after')}
                    className={CATALOG_ADD_CLASS}
                    title="Add after document"
                  >
                    After
                    <ArrowRightIcon className="size-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
